import { useCallback, useEffect, useRef, useState } from 'react'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { downloadFile } from '../../../../shared/helpers'

/**
 * Einen Kurs laden — samt Neuladen nach einer Meldung.
 *
 * `reload` liefert das Versprechen zurück, damit der Aufrufer nach dem Laden
 * weitermachen kann (etwa vom Quiz zurück in die Übersicht mit frischem Stand).
 */
/*
 * In der Vorschau (Verwaltung, Lehrkraft, „Als Schüler:in ansehen“) wird kein
 * Fortschritt gespeichert — ein Quiz, das erst nach allen gesehenen Lektionen
 * aufgeht, bliebe dort für immer zu. Wer den Kurs prüft, muss aber auch die
 * Fragen sehen können; der Server lässt das Quiz in der Vorschau ohnehin zu.
 */
export const vorschauOffen = data =>
	data?.mode === 'PREVIEW'
		? {
				...data,
				modules: (data.modules || []).map(modul =>
					modul.quiz ? { ...modul, quiz: { ...modul.quiz, unlocked: true } } : modul
				)
		  }
		: data

export const useCourse = id => {
	const [course, setCourse] = useState(null)
	const [error, setError] = useState(null)
	const mounted = useRef(true)

	useEffect(
		() => () => {
			mounted.current = false
		},
		[]
	)

	const reload = useCallback(
		() =>
			http
				.get(TApi.COURSE(id), { disableToast: true })
				.then(({ data }) => {
					if (mounted.current) {
						setCourse(vorschauOffen(data))
						setError(null)
					}
					return data
				})
				.catch(err => {
					if (mounted.current) setError(err?.response?.data?.message || err?.message || 'error')
					return null
				}),
		[id]
	)

	useEffect(() => {
		setCourse(null)
		reload()
	}, [reload])

	return { course, error, reload, setCourse }
}

/**
 * Alle Lektionen eines Kurses in einer Reihe — für „Weiter ansehen" und die
 * Folgenliste am Rand des Players.
 */
export const flatLessons = course =>
	(course?.modules || []).flatMap(modul => (modul.lessons || []).map(lesson => ({ ...lesson, moduleId: modul.id })))

/**
 * Wohin „Weiter ansehen" springt: die erste nicht erledigte Lektion, sonst das
 * erste freigeschaltete, aber noch nicht bestandene Quiz, sonst der Anfang.
 * Der Server nennt die Lektion (`progress.nextLessonId`); fehlt sie, rechnet
 * die Oberfläche selbst — nur für den Knopf, nicht für einen Stand.
 */
export const nextTarget = course => {
	if (!course) return null
	const lessons = flatLessons(course)

	const offeneLektion = course.progress?.nextLessonId
		? lessons.find(lesson => lesson.id === course.progress.nextLessonId)
		: lessons.find(lesson => !lesson.progress?.completed)

	if (offeneLektion) {
		// Liegt vor der offenen Lektion ein offenes Quiz, kommt erst das dran.
		const modulIndex = course.modules.findIndex(modul => modul.id === offeneLektion.moduleId)
		const offenesQuiz = course.modules
			.slice(0, modulIndex)
			.find(modul => modul.quiz?.available && modul.quiz?.unlocked && !modul.quiz?.passed)
		if (offenesQuiz) return { kind: 'quiz', moduleId: offenesQuiz.id }
		return { kind: 'lesson', lessonId: offeneLektion.id }
	}

	const quiz = (course.modules || []).find(modul => modul.quiz?.available && !modul.quiz?.passed)
	if (quiz) return { kind: 'quiz', moduleId: quiz.id }

	return lessons[0] ? { kind: 'lesson', lessonId: lessons[0].id, restart: true } : null
}

/** Die Urkunde angemeldet holen und speichern. */
export const downloadCertificate = (url, name = 'Urkunde.pdf') =>
	http.get(url, { responseType: 'blob' }).then(({ data }) => downloadFile(name, data))

export const certificateFileName = title =>
	`Urkunde-${String(title || 'Kurs')
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.replace(/[^A-Za-z0-9]+/g, '-')
		.replace(/^-|-$/g, '')}.pdf`
